import { useEffect, useState } from 'react'
import Dialog from '../../../../../dialog/Dialog'
import { Alert, Skeleton, Typography } from '@mui/material'
import ButtonStartIcon from '../../../../../button/ButtonStartIcon'
import { useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import DialogAttachMedia from '../../../../../dialog/DialogAttachMedia'
import ChipMedia from '../../../../../chip/ChipMedia'
import useNote from '../../../../../../hooks/maestro/useNote'
import { validate } from '../../../../../../utils'
import { handlerErrorV2 } from '../../../../../../utils/errors'
import useUpload from '../../../../../../hooks/upload/useUpload'
import { getNoteByLcIdAndLevelIdAndGsId } from '../../../../../../services/maestro/note.service'
import { useParams } from 'react-router-dom'
import TextEditor from '../../../../../TextEditor/TextEditor'
import ReadOnlyTextEditor from '../../../../../TextEditor/ReadOnlyTextEditor'
import DialogConfirmCancel from '../../../../../dialog/DialogConfirm'

const DialogNote = ({ onClose, open, formData, type, studentId: studentIdProp }) => {
  const types = ['create', 'edit', 'delete', 'create-comment', 'edit-comment', 'delete-comment']
  const { curriculumId, ulId } = useParams()
  const [form, setForm] = useState(formData)
  const [errors, setError] = useState(null)
  const [modal, setModal] = useState(null)
  const [loading, setLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingDelete, setIsLoadingDelete] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const student = useSelector((state) => state?.classMarking?.student)
  const studentId = studentIdProp || student?._id
  const permissions = useSelector((state) => state?.session?.permissions?.['marking-class'] || {})
  const completed = useSelector((state) => state?.classMarking.completedType)

  const currentMode = useSelector((state) =>
    state?.classMarking?.mode?.find((item) => student?._id === item.studentId)
  )
  const alertSave = useSelector((state) => state?.alert?.save || {})

  const { deleteFiles, uploadFile } = useUpload()

  const { createNoteByLcIdAndLevelId, deleteNote } = useNote()

  const onConfirm = async (e) => {
    e.preventDefault()

    if (currentMode && alertSave.mode && alertSave.onSave) {
      alertSave.onSave()
    }

    if (!types.includes(type)) {
      setError({ message: 'Invalid type' })
      return
    }
    const fieldRules = {
      comment: 'required|string'
    }
    if (form.comment === '<p><br></p>') {
      form.comment = ''
    }
    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      const message = errors.message || errors.comment || 'Some fields are required or invalid'
      setError({ ...errors, message })
    } else {
      setLoading(true)
      try {
        let result = { error: false }
        if (['create', 'edit', 'create-comment', 'edit-comment'].includes(type)) {
          const media = form.media
            ?.filter((item) => item.type === 'file' && item.file)
            ?.map((item) => ({
              ...item,
              id: item._id || item.id,
              type: item.mimetype
            }))

          if (media?.length > 0) {
            const resultUpload = await uploadFile(media, 'uploads')
            if (resultUpload.error) {
              setError(resultUpload)
              return
            }
            const newMedia = resultUpload.data
            form.media = form.media
              .map((item) => {
                if (item.type === 'file') {
                  const find = newMedia.find((media) => media.id === item.id)
                  if (find) {
                    return { ...item, ...find }
                  }
                }

                return item
              })
              .filter((item) => item.link)
          }
        }
        if (['delete-comment', 'delete'].includes(type)) {
          const media = form.media.filter((item) => item.type === 'file')

          if (media.length > 0) {
            const resultDelete = await deleteFiles(media, 'uploads')
            if (resultDelete.error) {
              setError(resultDelete)
              return
            }
          }
        }

        if (['create', 'edit'].includes(type)) {
          result = await createNoteByLcIdAndLevelId(
            studentId,
            formData.lcId,
            formData.levelId,
            formData.gsId,
            form
          )
        }
        if (type === 'delete') {
          result = await deleteNote(studentId, form._id)
        }

        if (result.error) {
          setError(result)
        } else {
          onClose()
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        setError({ message })
      } finally {
        setLoading(false)
      }
    }
  }

  const onDelete = async (id) => {
    setIsLoadingDelete(true)
    try {
      const result = await deleteNote(studentId, id)
      if (result.error) {
        setError(result)
      } else {
        onClose()
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      setError({ error: true, message })
    } finally {
      setIsLoadingDelete(false)
    }
  }
  const onSaveMedia = async (type, data) => {
    const currentMedia = form.media || []
    if (type === 'add') {
      setForm({
        ...form,
        media: [
          ...currentMedia,
          {
            id: data._id || new Date().getTime(),
            ...data
          }
        ]
      })
    } else {
      const media = currentMedia
        .map((item) => ({ id: item._id || item.id, ...item }))
        .filter((item) => item.id !== data.id)
      setForm({ ...form, media })
      if (data.type === 'file') {
        const result = await deleteFiles([data], 'uploads')
        if (result.error) {
          setError(result)
        }
      }
    }
  }

  const onChange = (key, value) => {
    setForm({ ...form, [key]: value })
    if (key === 'comment') {
      setError({ ...errors, [key]: null, message: null })
    } else {
      setError({ ...errors, [key]: null })
    }
  }

  const onLoad = async () => {
    setIsLoading(true)
    try {
      const response = await getNoteByLcIdAndLevelIdAndGsId(
        curriculumId,
        studentId,
        formData.lcId,
        formData.levelId,
        formData.gsId,
        formData.gsId.indexOf('gs-0') > -1 ? 'main' : ulId
      )
      if (!response.data && response.error) {
        setForm({ ...formData, comment: '' })
      } else {
        setForm(response.data)
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      if (['Note of progress not found', 'Note not found'].includes(message)) {
        setForm({ ...formData, comment: '' })
      } else {
        setError({ error: true, message })
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      onLoad()
    }
  }, [open])

  const commentIsEmpty =
    !form?.comment ||
    form?.comment === '<p><br></p>' ||
    form?.comment === '<p></p>' ||
    form?.comment === '<p><br></p><p></p>'

  const Actions = (
    <div className='w-full flex gap-4 justify-between'>
      <div className='flex gap-4 justify-end items-center'>
        {/* <ButtonStartIcon
          onClick={() => setModal({ form: {}, type: 'add' })}
          color='primary'
          variant='text'
          disabled={loading || commentIsEmpty}
          startIcon={'link'}
        >
          Attach media
        </ButtonStartIcon> */}
      </div>
      <div className='flex gap-4 justify-end'>
        {permissions['by-teacher'] && completed === 'completedTeacher' && form?._id && (
          <>
            <ButtonStartIcon
              color='error'
              variant='contained'
              onClick={() => setConfirmDelete(true)}
              disabled={loading || isLoadingDelete}
              loading={isLoadingDelete}
              startIcon={['fal', 'check']}
            >
              delete
            </ButtonStartIcon>
            <div className='px-2' />
          </>
        )}
        <ButtonStartIcon
          onClick={onClose}
          color='primary'
          variant='outlined'
          disabled={loading}
          startIcon={['fal', 'times']}
        >
          Cancel
        </ButtonStartIcon>
        {permissions['by-teacher'] && completed === 'completedTeacher' && (
          <ButtonStartIcon
            onClick={onConfirm}
            color='primary'
            variant='contained'
            disabled={loading || commentIsEmpty}
            loading={loading}
            startIcon={['fal', 'check']}
          >
            Save
          </ButtonStartIcon>
        )}
      </div>
    </div>
  )
  return (
    <>
      <Dialog
        id='note'
        show={open}
        title={`Note`}
        onCloseHeader={permissions['by-teacher'] && completed === 'completedTeacher' ? false : true}
        maxWidth={'md'}
        actions={permissions['by-teacher'] && completed === 'completedTeacher' ? Actions : null}
        onClose={onClose}
      >
        {isLoading ? (
          <div className='h-screen-1/2'>
            <Skeleton height={'10%'} variant='rounded' />
            <Skeleton height={'90%'} variant='rounded' className='mt-2' />
          </div>
        ) : (
          <div className='flex flex-col gap-2'>
            {permissions['by-teacher'] && completed === 'completedTeacher' ? (
              <TextEditor
                content={form.comment}
                setContent={(value) => onChange('comment', value)}
                isEditable={permissions['by-teacher'] && completed === 'completedTeacher' ? true : false}
                showMenuBar={permissions['by-teacher'] && completed === 'completedTeacher' ? true : false}
                placeholder='The teacher has not entered any information yet'
                // editorVariant={editorVariant}
                // error={error}
              />
            ) : (
              <ReadOnlyTextEditor content={form.comment} />
            )}

            {form.media && form.media.length > 0 && (
              <div className='flex flex-wrap gap-4 items-center'>
                {form.media.map((data, index) => {
                  return (
                    <ChipMedia
                      key={`media-${index}-${data._id}-${data.type}`}
                      index={index}
                      data={data}
                      disabled={loading}
                      onDelete={
                        permissions['by-teacher'] && completed === 'completedTeacher'
                          ? () =>
                              setModal({
                                type: 'delete',
                                form: {
                                  id: data._id || data.id,
                                  ...data
                                }
                              })
                          : undefined
                      }
                    />
                  )
                })}
              </div>
            )}
            {errors && errors.message && (
              <Alert severity='error' onClose={() => setError({ message: null })}>
                {errors.message}
              </Alert>
            )}
          </div>
        )}
      </Dialog>
      {modal && (
        <DialogAttachMedia
          open={!!modal}
          onClose={() => setModal(false)}
          formData={modal.form}
          onSave={(data) => onSaveMedia(modal.type, data)}
          type={modal.type}
        />
      )}
      {confirmDelete && (
        <DialogConfirmCancel
          id='confirm-delete-note'
          open={confirmDelete}
          loading={loading}
          onCancel={() => setConfirmDelete(false)}
          textCancel='Cancel'
          onConfirm={() => {
            onDelete(form._id)
            setConfirmDelete(false)
          }}
          title='Delete note'
          maxWidth='xs'
          onCloseHeader={() => setConfirmDelete(false)}
          showActions={true}
        >
          <Typography component={'div'}>Are you sure you want to delete this note?</Typography>
        </DialogConfirmCancel>
      )}
    </>
  )
}

export default DialogNote
